import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../apiBase';
import SportTypeSelector from '../components/SportTypeSelector';
import { markFreelanceCoach } from '../utils/roles';
import { redirectAfterAuth } from '../utils/profileSession';
import ParentLinkChild from '../components/parent/ParentLinkChild';
import Logo from '../components/Logo';
import { useTranslation } from 'react-i18next';


export default function RegisterPage() {
    const { t } = useTranslation(['registerPage', 'common', 'nav', 'messages', 'errors', 'auth']);
    const ROLES = useMemo(() => [
        { value: 'Owner', icon: '🏫', label: t('registerPage:roleOwner'), desc: t('registerPage:roleOwnerDesc') },
        { value: 'Coach', icon: '🎽', label: t('registerPage:roleCoach'), desc: t('registerPage:roleCoachDesc') },
        { value: 'FreelanceCoach', icon: '👤', label: t('registerPage:roleFreelance'), desc: t('registerPage:roleFreelanceDesc') },
        { value: 'Member', icon: '⚽', label: t('registerPage:roleMember'), desc: t('registerPage:roleMemberDesc') },
        { value: 'Parent', icon: '👨‍👩‍👧', label: t('common:roles.parent'), desc: t('registerPage:roleParentDesc') },
    ], [t]);
    const [step, setStep] = useState(1);
    const [role, setRole] = useState('Member');
    const [authMethod, setAuthMethod] = useState('phone');
    const [form, setForm] = useState({ username: '', email: '', phone: '', password: '' });
    const [otpCode, setOtpCode] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [otpChannel, setOtpChannel] = useState(null);
    const [otpResendTimer, setOtpResendTimer] = useState(0);
    const [smsFallbackSending, setSmsFallbackSending] = useState(false);
    const [otpSending, setOtpSending] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [registered, setRegistered] = useState(false);
    const [consentGiven, setConsentGiven] = useState(false);
    const [consentHighlight, setConsentHighlight] = useState(false);
    const [sportTypeId, setSportTypeId] = useState('');
    const [sportTypeIds, setSportTypeIds] = useState([]);
    const [sportLabel, setSportLabel] = useState('');
    const [city, setCity] = useState('');
    const [yearsExperience, setYearsExperience] = useState('');
    const [bio, setBio] = useState('');

    const isFreelance = role === 'FreelanceCoach';
    const isParent = role === 'Parent';
    const isRuServer = typeof window !== 'undefined'
        && window.location.hostname.includes('sportprogress.ru');
    const ruEmailHint = (isRuServer && authMethod === 'email' && form.email
        && !form.email.trim().toLowerCase().endsWith('.ru'))
        ? t('auth:ruEmailRequired')
        : null;

    useEffect(() => {
        if (role === 'Parent') setAuthMethod('email');
    }, [role]);

    useEffect(() => {
        if (otpResendTimer <= 0) return;
        const timerId = setInterval(() => setOtpResendTimer(s => Math.max(0, s - 1)), 1000);
        return () => clearInterval(timerId);
    }, [otpResendTimer]);

    const otpMaxLength = otpChannel === 'email' ? 6 : 4;

    function getOtpPlaceholder() {
        if (otpChannel === 'call') return t('auth:callOtpCodePlaceholder');
        if (otpChannel === 'sms') return t('auth:smsOtpPlaceholder');
        if (otpChannel === 'email') return t('auth:emailOtpPlaceholder');
        return t('registerPage:otpPlaceholder');
    }

    function applyOtpSendResponse(data) {
        const channel = data?.channel || (authMethod === 'email' ? 'email' : 'sms');
        setOtpChannel(channel);
        setOtpSent(true);
        const waitSec = channel === 'call'
            ? (data?.fallbackSmsAvailableAfterSeconds ?? 45)
            : 60;
        setOtpResendTimer(waitSec);
    }

    async function handleRequestOtp() {
        setOtpSending(true);
        setError('');
        try {
            const payload = authMethod === 'phone'
                ? { phone: form.phone }
                : { email: form.email };
            const res = await axios.post(`${getApiBaseUrl()}/auth/request-register-otp`, payload);
            applyOtpSendResponse(res.data);
        } catch (err) {
            const data = err.response?.data;
            if (data?.error === 'email_domain_not_allowed') {
                setError(t('auth:ruEmailRequired'));
            } else {
                setError(data?.message || data?.error || t('registerPage:errOtpSend'));
            }
        }
        setOtpSending(false);
    }

    async function requestSmsFallback() {
        if (authMethod !== 'phone' || !form.phone.trim()) return;
        setSmsFallbackSending(true);
        setError('');
        try {
            await axios.post(`${getApiBaseUrl()}/auth/request-otp-sms-fallback`, { phone: form.phone });
            setOtpChannel('sms');
            setOtpResendTimer(60);
        } catch (err) {
            const data = err.response?.data;
            setError(data?.message || data?.error || t('registerPage:errOtpSend'));
        }
        setSmsFallbackSending(false);
    }

    const inputStyle = {
        width: '100%', padding: '12px 14px', borderRadius: '10px',
        border: '1.5px solid var(--sp-gray-200)', fontSize: '14px',
        outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.2s',
        fontFamily: 'inherit'
    };

    async function handleRegister(e) {
        e.preventDefault();
        if (!consentGiven) {
            setConsentHighlight(true);
            setError(t('registerPage:errConsent'));
            return;
        }
        setLoading(true);
        setError('');
        setConsentHighlight(false);
        if (role === 'Owner' && !sportTypeId) {
            setError(t('registerPage:errSportRequired'));
            setLoading(false);
            return;
        }
        if (isFreelance) {
            if (sportTypeIds.length === 0) {
                setError(t('registerPage:errSportMulti'));
                setLoading(false);
                return;
            }
            if (!city.trim()) {
                setError(t('registerPage:errCity'));
                setLoading(false);
                return;
            }
        }
        if (!otpCode.trim()) {
            setError(t('registerPage:errOtpRequired'));
            setLoading(false);
            return;
        }
        try {
            const payload = {
                username: form.username,
                password: form.password,
                role: role,
                sportTypeId: sportTypeId ? parseInt(sportTypeId, 10) : null,
                consentGiven: true,
                otpCode: otpCode.trim(),
            };
            if (authMethod === 'phone') {
                payload.phone = form.phone;
                if (form.email.trim()) payload.email = form.email.trim();
            } else {
                payload.email = form.email.trim();
            }
            if (isFreelance) {
                payload.sportTypeIds = sportTypeIds.map((id) => parseInt(id, 10));
                payload.city = city.trim();
                payload.yearsExperience = yearsExperience ? parseInt(yearsExperience, 10) : 0;
                if (bio.trim()) payload.bio = bio.trim();
            }
            const res = await axios.post(`${getApiBaseUrl()}/auth/register`, payload);
            if (res.data.token || res.data.requireProfileSelect) {
                if (role === 'Parent') {
                    localStorage.setItem('accountRole', 'Parent');
                    redirectAfterAuth(res.data, '/parent/link-child');
                    return;
                }
                redirectAfterAuth(res.data);
                return;
            }
            if (res.data.sportTypeId) {
                localStorage.setItem('sportTypeId', res.data.sportTypeId);
                localStorage.setItem('sportType', res.data.sportType || '');
                localStorage.setItem('sportIcon', res.data.sportIcon || '');
            }
            if (res.data.isFreelance) markFreelanceCoach();
            else localStorage.removeItem('isFreelance');
            if (res.data.role) {
                localStorage.setItem('accountRole', res.data.role);
            }
            setRegistered(true);
        } catch (err) {
            const data = err.response?.data;
            if (!err.response) {
                setError(t('registerPage:errNoResponse'));
            } else if (data?.error === 'email_domain_not_allowed') {
                setError(t('auth:ruEmailRequired'));
            } else {
                setError(data?.message || data?.error || t('registerPage:errRegister', { status: err.response.status }));
            }
        }
        setLoading(false);
    }

    function handleSubmitClick(e) {
        if (!consentGiven) {
            e.preventDefault();
            setConsentHighlight(true);
            setError(t('registerPage:errConsent'));
        }
    }

    // После регистрации Owner — сразу на логин
    // Coach/Member — показываем сообщение о подаче заявки
    if (registered) {
        return (
            <div className="auth-page page-register" style={{
                minHeight: '100vh', background: 'linear-gradient(135deg, #1A1A1A 0%, #3d1515 100%)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
                fontFamily: "'Segoe UI', Arial, sans-serif"
            }}>
                <div className="auth-page__inner" style={{ width: '100%', maxWidth: '460px' }}>
                    <div className="auth-card" style={{
                        background: 'white', borderRadius: '20px',
                        padding: '36px', textAlign: 'center',
                        boxShadow: '0 24px 60px rgba(0,0,0,0.3)'
                    }}>
                        <div style={{ fontSize: '56px', marginBottom: '16px' }}>
                            {role === 'Owner' ? '🏫' : role === 'FreelanceCoach' ? '👤' : '✅'}
                        </div>
                        <h2 style={{ margin: '0 0 12px', fontSize: '22px' }}>
                            {t('registerPage:successTitle')}
                        </h2>

                        {role === 'Owner' ? (
                            <>
                                <p style={{ color: '#666', marginBottom: '24px' }}>
                                    {t('registerPage:ownerHint')}
                                </p>
                                <a href="/login" style={{
                                    display: 'block', padding: '14px',
                                    background: '#1A1A1A', color: 'white',
                                    borderRadius: '10px', textDecoration: 'none',
                                    fontWeight: '700', fontSize: '15px'
                                }}>
                                    {t('registerPage:loginLink')}
                                </a>
                            </>
                        ) : role === 'FreelanceCoach' ? (
                            <>
                                <p style={{ color: '#666', marginBottom: '8px' }}>
                                    {t('registerPage:freelanceHint')}
                                </p>
                                <p style={{ color: '#888', fontSize: '13px', marginBottom: '24px' }}>
                                    {t('registerPage:freelanceHint2')}
                                </p>
                                <a href="/login" style={{
                                    display: 'block', padding: '14px',
                                    background: '#EB3333', color: 'white',
                                    borderRadius: '10px', textDecoration: 'none',
                                    fontWeight: '700', fontSize: '15px'
                                }}>
                                    {t('registerPage:loginCabinet')}
                                </a>
                            </>
                        ) : isParent ? (
                            <>
                                <p style={{ color: '#666', marginBottom: '8px' }}>
                                    {t('registerPage:parentHint')}
                                </p>
                                <ParentLinkChild />
                                <a href="/login" style={{
                                    display: 'block', padding: '14px', marginTop: '16px',
                                    background: '#EB3333', color: 'white',
                                    borderRadius: '10px', textDecoration: 'none',
                                    fontWeight: '700', fontSize: '15px', textAlign: 'center'
                                }}>
                                    {t('registerPage:loginCabinet')}
                                </a>
                            </>
                        ) : (
                            <>
                                <p style={{ color: '#666', marginBottom: '8px' }}>
                                    {t('registerPage:applyHint')}
                                </p>
                                <p style={{ color: '#888', fontSize: '13px', marginBottom: '24px' }}>
                                    {role === 'Coach'
                                        ? t('registerPage:coachApplyHint')
                                        : t('registerPage:memberApplyHint')
                                    }
                                </p>
                                <a href="/login" style={{
                                    display: 'block', padding: '14px',
                                    background: '#EB3333', color: 'white',
                                    borderRadius: '10px', textDecoration: 'none',
                                    fontWeight: '700', fontSize: '15px'
                                }}>
                                    {t('registerPage:loginApply')}
                                </a>
                            </>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-page page-register" style={{
            minHeight: '100vh', background: 'linear-gradient(135deg, #1A1A1A 0%, #3d1515 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
            fontFamily: "'Segoe UI', Arial, sans-serif"
        }}>
            <div className="auth-page__inner" style={{ width: '100%', maxWidth: '460px' }}>

                <div className="auth-page__hero" style={{ textAlign: 'center', marginBottom: '28px' }}>
                    <Logo variant="dark" height={44} style={{ margin: '0 auto 16px' }} />
                    <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '6px' }}>
                        {t('registerPage:heroTitle')}
                    </p>
                </div>

                <div className="auth-card" style={{
                    background: 'white', borderRadius: '20px',
                    padding: '36px', boxShadow: '0 24px 60px rgba(0,0,0,0.3)'
                }}>

                    {/* Индикатор шагов */}
                    <div style={{ display: 'flex', gap: '8px', marginBottom: '28px' }}>
                        {[1, 2].map(s => (
                            <div key={s} style={{
                                flex: 1, height: '4px', borderRadius: '2px',
                                background: s <= step ? '#EB3333' : 'var(--sp-gray-200)',
                                transition: 'background 0.3s'
                            }} />
                        ))}
                    </div>

                    {/* ШАГ 1 — выбор роли */}
                    {step === 1 && (
                        <>
                            <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '700' }}>
                                {t('registerPage:stepWho')}
                            </h2>
                            <p style={{ color: '#888', fontSize: '14px', margin: '0 0 24px' }}>
                                {t('registerPage:stepWhoDesc')}
                            </p>
                            <div className="register-roles-grid responsive-grid" style={{ display: 'grid', gap: '10px', marginBottom: '24px' }}>
                                {ROLES.map(r => (
                                    <div
                                        key={r.value}
                                        onClick={() => setRole(r.value)}
                                        style={{
                                            padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                                            border: `2px solid ${role === r.value ? '#EB3333' : 'var(--sp-gray-200)'}`,
                                            background: role === r.value ? '#f0ebff' : 'white',
                                            display: 'flex', alignItems: 'center', gap: '12px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <span style={{ fontSize: '28px' }}>{r.icon}</span>
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontWeight: '600', fontSize: '14px' }}>{r.label}</div>
                                            <div style={{ color: '#888', fontSize: '12px' }}>{r.desc}</div>
                                        </div>
                                        {role === r.value && (
                                            <span style={{ color: '#EB3333', fontWeight: 'bold', fontSize: '18px' }}>✓</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                            <button
                                onClick={() => setStep(2)}
                                style={{
                                    width: '100%', padding: '13px', background: '#1A1A1A',
                                    color: 'white', border: 'none', borderRadius: '10px',
                                    fontSize: '15px', fontWeight: '700', cursor: 'pointer'
                                }}
                            >
                                {t('registerPage:continue')}
                            </button>
                        </>
                    )}

                    {/* ШАГ 2 — данные аккаунта */}
                    {step === 2 && (
                        <>
                            <h2 style={{ margin: '0 0 6px', fontSize: '20px', fontWeight: '700' }}>
                                {t('registerPage:stepAccount')}
                            </h2>
                            <p style={{ color: '#888', fontSize: '14px', margin: '0 0 24px' }}>
                                {role === 'Owner'
                                    ? t('registerPage:stepOwnerAfter')
                                    : isFreelance
                                        ? t('registerPage:stepFreelanceAfter')
                                        : t('registerPage:stepApplyAfter')
                                }
                            </p>

                            <form onSubmit={handleRegister}>
                                <div style={{ marginBottom: '14px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {role === 'Owner' ? t('registerPage:nameOwner') : isFreelance ? t('registerPage:nameFreelance') : t('registerPage:nameDefault')}
                                    </label>
                                    <input
                                        value={form.username}
                                        onChange={e => setForm({ ...form, username: e.target.value })}
                                        placeholder={role === 'Owner' ? t('registerPage:placeholderOwner') : isFreelance ? t('registerPage:placeholderFreelance') : t('registerPage:placeholderName')}
                                        required style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>
                                {role === 'Owner' && (
                                    <div style={{ marginBottom: '14px' }}>
                                        <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '8px' }}>
                                            {t('registerPage:sportSchool')}
                                        </label>
                                        {sportLabel && (
                                            <p style={{ fontSize: 13, color: '#EB3333', margin: '0 0 8px', fontWeight: 600 }}>
                                                {t('registerPage:selected', { label: sportLabel })}
                                            </p>
                                        )}
                                        <SportTypeSelector
                                            value={sportTypeId}
                                            compact
                                            onChange={(id, sport) => {
                                                setSportTypeId(String(id));
                                                setSportLabel(sport ? `${sport.emoji} ${sport.name}` : '');
                                            }}
                                        />
                                    </div>
                                )}
                                {isFreelance && (
                                    <>
                                        <div style={{ marginBottom: '14px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '8px' }}>
                                                {t('registerPage:sportsMulti')}
                                            </label>
                                            {sportTypeIds.length > 0 && (
                                                <p style={{ fontSize: 13, color: '#EB3333', margin: '0 0 8px', fontWeight: 600 }}>
                                                    {t('registerPage:selectedCount', { count: sportTypeIds.length })}
                                                </p>
                                            )}
                                            <SportTypeSelector
                                                multiple
                                                values={sportTypeIds}
                                                compact
                                                onChange={(ids) => setSportTypeIds(ids.map(String))}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '14px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                                {t('registerPage:city')}
                                            </label>
                                            <input
                                                value={city}
                                                onChange={e => setCity(e.target.value)}
                                                placeholder={t('registerPage:cityPlaceholder')}
                                                required
                                                style={inputStyle}
                                                onFocus={e => e.target.style.borderColor = '#EB3333'}
                                                onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '14px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                                {t('registerPage:experience')}
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                max="60"
                                                value={yearsExperience}
                                                onChange={e => setYearsExperience(e.target.value)}
                                                placeholder="5"
                                                style={inputStyle}
                                                onFocus={e => e.target.style.borderColor = '#EB3333'}
                                                onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                            />
                                        </div>
                                        <div style={{ marginBottom: '14px' }}>
                                            <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                                {t('registerPage:bio')}
                                            </label>
                                            <textarea
                                                value={bio}
                                                onChange={e => setBio(e.target.value)}
                                                placeholder={t('registerPage:bioPlaceholder')}
                                                maxLength={500}
                                                rows={3}
                                                style={{ ...inputStyle, resize: 'vertical', minHeight: '72px' }}
                                                onFocus={e => e.target.style.borderColor = '#EB3333'}
                                                onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                            />
                                        </div>
                                    </>
                                )}
                                <div style={{ marginBottom: '14px' }}>
                                    <div className="auth-method-switcher" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                                        <button
                                            type="button"
                                            onClick={() => { setAuthMethod('phone'); setOtpSent(false); }}
                                            style={{
                                                flex: 1, padding: '10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                                background: authMethod === 'phone' ? '#1A1A1A' : '#f1f5f9',
                                                color: authMethod === 'phone' ? 'white' : '#444',
                                                fontWeight: 600, fontSize: 13,
                                            }}
                                        >
                                            📱 {t('auth:byPhone')}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setAuthMethod('email'); setOtpSent(false); }}
                                            style={{
                                                flex: 1, padding: '10px', borderRadius: 8, border: 'none', cursor: 'pointer',
                                                background: authMethod === 'email' ? '#1A1A1A' : '#f1f5f9',
                                                color: authMethod === 'email' ? 'white' : '#444',
                                                fontWeight: 600, fontSize: 13,
                                            }}
                                        >
                                            ✉️ {t('auth:byEmail')}
                                        </button>
                                    </div>
                                    {authMethod === 'phone' ? (
                                        <input
                                            type="tel"
                                            value={form.phone}
                                            onChange={e => setForm({ ...form, phone: e.target.value })}
                                            placeholder="+7 900 000-00-00"
                                            required
                                            style={inputStyle}
                                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                        />
                                    ) : (
                                        <input
                                            type="email"
                                            value={form.email}
                                            onChange={e => setForm({ ...form, email: e.target.value })}
                                            placeholder="your@email.com"
                                            required
                                            style={inputStyle}
                                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                        />
                                    )}
                                    {ruEmailHint ? (
                                        <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#b45309', lineHeight: 1.5 }}>
                                            ⚠️ {ruEmailHint}
                                        </p>
                                    ) : null}
                                </div>
                                <div style={{ marginBottom: '14px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('registerPage:otpLabel')}
                                    </label>
                                    {otpSent && otpChannel === 'call' ? (
                                        <div
                                            className="call-otp-hint"
                                            style={{
                                                display: 'flex',
                                                gap: 12,
                                                alignItems: 'flex-start',
                                                padding: '12px 14px',
                                                marginBottom: 12,
                                                borderRadius: 10,
                                                background: '#eff6ff',
                                                border: '1px solid #bfdbfe',
                                            }}
                                        >
                                            <div className="call-otp-hint__icon" style={{ fontSize: 22, lineHeight: 1 }}>📞</div>
                                            <div className="call-otp-hint__text" style={{ flex: 1 }}>
                                                <strong style={{ display: 'block', marginBottom: 4, color: '#1e3a5f' }}>
                                                    {t('auth:callOtpTitle')}
                                                </strong>
                                                <p style={{ margin: 0, fontSize: 12, color: '#475569', lineHeight: 1.5 }}>
                                                    {t('auth:callOtpDescription')}
                                                </p>
                                            </div>
                                        </div>
                                    ) : otpSent && otpChannel === 'sms' ? (
                                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                                            {t('auth:smsOtpSentHint')}
                                        </p>
                                    ) : otpSent && otpChannel === 'email' ? (
                                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                                            {t('auth:emailOtpSentHint')}
                                        </p>
                                    ) : (
                                        <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                                            {t('registerPage:otpHint')}
                                        </p>
                                    )}
                                    <div className="register-otp-row" style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            autoComplete="one-time-code"
                                            maxLength={otpMaxLength}
                                            value={otpCode}
                                            onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, otpMaxLength))}
                                            placeholder={getOtpPlaceholder()}
                                            required
                                            aria-label={t('registerPage:otpLabel')}
                                            className="register-otp-input"
                                            style={{
                                                ...inputStyle,
                                                flex: 1,
                                                marginBottom: 0,
                                                fontSize: '18px',
                                                fontWeight: 700,
                                                letterSpacing: '4px',
                                                textAlign: 'center',
                                            }}
                                            onFocus={e => e.target.style.borderColor = '#EB3333'}
                                            onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                        />
                                        <button
                                            type="button"
                                            onClick={handleRequestOtp}
                                            disabled={otpSending || (otpSent && otpResendTimer > 0) || (otpSent && otpChannel === 'call')}
                                            style={{
                                                padding: '0 14px', borderRadius: 10, border: 'none',
                                                background: '#EB3333', color: 'white', fontWeight: 600,
                                                cursor: otpSending ? 'wait' : 'pointer', whiteSpace: 'nowrap',
                                                minHeight: 48,
                                                opacity: (otpSent && (otpResendTimer > 0 || otpChannel === 'call')) ? 0.6 : 1,
                                                display: (otpSent && otpChannel === 'call') ? 'none' : 'block',
                                            }}
                                        >
                                            {otpSent ? t('registerPage:otpResend') : t('auth:getCode')}
                                        </button>
                                    </div>
                                    {otpSent ? (
                                        <>
                                            {otpResendTimer > 0 ? (
                                                <p style={{ margin: '8px 0 0', fontSize: '12px', color: '#64748b' }}>
                                                    {t('auth:resendIn', { sec: String(otpResendTimer).padStart(2, '0') })}
                                                </p>
                                            ) : null}
                                            {otpChannel === 'call' && otpResendTimer === 0 ? (
                                                <button
                                                    type="button"
                                                    onClick={requestSmsFallback}
                                                    disabled={smsFallbackSending}
                                                    style={{
                                                        marginTop: 8,
                                                        background: 'none',
                                                        border: 'none',
                                                        color: '#EB3333',
                                                        cursor: smsFallbackSending ? 'wait' : 'pointer',
                                                        fontWeight: 600,
                                                        fontSize: 13,
                                                        padding: 0,
                                                    }}
                                                >
                                                    {t('auth:requestSmsFallback')}
                                                </button>
                                            ) : null}
                                        </>
                                    ) : null}
                                </div>
                                <div style={{ marginBottom: '24px' }}>
                                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#444', marginBottom: '6px' }}>
                                        {t('auth:password')}
                                    </label>
                                    <input
                                        type="password" value={form.password}
                                        onChange={e => setForm({ ...form, password: e.target.value })}
                                        placeholder={t('registerPage:passwordPlaceholder')}
                                        required style={inputStyle}
                                        onFocus={e => e.target.style.borderColor = '#EB3333'}
                                        onBlur={e => e.target.style.borderColor = 'var(--sp-gray-200)'}
                                    />
                                </div>

                                <div style={{
                                    marginBottom: '16px', padding: '12px',
                                    borderRadius: '10px',
                                    border: consentHighlight ? '2px solid #c62828' : '1px solid var(--sp-gray-200)',
                                    background: consentGiven ? '#f0fdf4' : '#fafafa'
                                }}>
                                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '13px' }}>
                                        <input
                                            type="checkbox"
                                            checked={consentGiven}
                                            onChange={e => {
                                                setConsentGiven(e.target.checked);
                                                setConsentHighlight(false);
                                                if (e.target.checked) setError('');
                                            }}
                                            style={{ marginTop: '3px' }}
                                        />
                                        <span>
                                            {t('registerPage:consent')}{' '}
                                            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                                                style={{ color: '#EB3333', fontWeight: '600' }}
                                                onClick={e => e.stopPropagation()}>
                                                {t('registerPage:privacyPolicy')}
                                            </a>
                                        </span>
                                    </label>
                                    {consentHighlight && (
                                        <div style={{ color: '#c62828', fontSize: '12px', marginTop: '8px' }}>
                                            {t('registerPage:errConsent')}
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div style={{
                                        background: '#fce4ec', color: '#c62828',
                                        padding: '10px 14px', borderRadius: '8px',
                                        fontSize: '13px', marginBottom: '16px'
                                    }}>
                                        ⚠️ {error}
                                    </div>
                                )}

                                <div className="form-row" style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        type="button" onClick={() => setStep(1)}
                                        className="btn-full-mobile"
                                        style={{
                                            padding: '13px 20px', background: 'white',
                                            color: '#333', border: '1.5px solid var(--sp-gray-200)',
                                            borderRadius: '10px', fontSize: '14px', cursor: 'pointer'
                                        }}
                                    >
                                        ← {t('common:back')}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={loading || !consentGiven}
                                        onClick={handleSubmitClick}
                                        className="form-submit btn-full-mobile"
                                        style={{
                                            flex: 1, padding: '13px',
                                            background: loading || !consentGiven ? '#94a3b8' : '#1A1A1A',
                                            color: 'white', border: 'none', borderRadius: '10px',
                                            fontSize: '15px', fontWeight: '700',
                                            cursor: loading || !consentGiven ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loading ? t('registerPage:creating') : t('registerPage:createAccount')}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}

                    <div style={{
                        textAlign: 'center', marginTop: '20px', paddingTop: '20px',
                        borderTop: '1px solid #f0f0f0', fontSize: '14px', color: '#888'
                    }}>
                        {t('registerPage:haveAccount')}{' '}
                        <a href="/login" style={{ color: '#EB3333', fontWeight: '600', textDecoration: 'none' }}>
                            {t('registerPage:login')}
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
}